(() => {
  'use strict';

  // Theme section reloads or a repeated script include must not add handlers twice.
  if (window.__l7MeasurementLoaded) return;
  window.__l7MeasurementLoaded = true;

  const MEASUREMENT_ID = 'G-RBMXXWTL5M';
  const APP_STORE_HOST = 'apps.shopify.com';
  const APP_STORE_PATH = '/liva-7';
  const QA_QUERY_PARAMETERS = new Set(['l7_qa', 'l7_measurement_probe', 'l7_measurement_test']);
  const LEAD_CONFIRMATION_RETRY_DELAYS = [0, 100, 500, 1500, 4000, 10000];
  const LEAD_ATTEMPT_KEY = 'l7m:pilot-submission:v1';
  const LEAD_ATTEMPT_MAX_AGE = 24 * 60 * 60 * 1000;
  const leadDocumentId = randomEventId('page');
  const preparedForms = new WeakSet();
  let leadAttempt;
  let googleInitialised = false;
  let customerPrivacyLoadStarted = false;

  function isQaVisit() {
    const query = new URLSearchParams(window.location.search);
    return [...QA_QUERY_PARAMETERS].some((name) => query.has(name));
  }

  function setQaStatus(name, value) {
    if (!isQaVisit()) return;
    document.documentElement.setAttribute(`data-l7-${name}`, String(value));
  }

  function analyticsAllowed() {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;
    } catch (_error) {
      return false;
    }
  }

  function handleCustomerPrivacyReady() {
    setQaStatus('privacy-api', 'ready');
    setQaStatus('analytics-allowed', analyticsAllowed());
    if (!analyticsAllowed()) return;
    initialiseGoogleTag();
    scheduleServerConfirmedLeadRecording();
  }

  function loadCustomerPrivacyApi() {
    if (window.Shopify?.customerPrivacy) {
      handleCustomerPrivacyReady();
      return;
    }
    if (customerPrivacyLoadStarted) return;
    if (typeof window.Shopify?.loadFeatures !== 'function') {
      setQaStatus('privacy-api', 'loader-unavailable');
      return;
    }

    customerPrivacyLoadStarted = true;
    setQaStatus('privacy-api', 'loading');
    window.Shopify.loadFeatures([
      { name: 'consent-tracking-api', version: '0.1' },
    ], (error) => {
      if (error) {
        setQaStatus('privacy-api', 'error');
        customerPrivacyLoadStarted = false;
        return;
      }
      handleCustomerPrivacyReady();
    });
  }

  function initialiseGoogleTag() {
    if (isQaVisit() || !analyticsAllowed()) return false;

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() {
      window.dataLayer.push(arguments);
    };

    if (!googleInitialised) {
      window.gtag('consent', 'default', {
        analytics_storage: 'granted',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
      window.gtag('set', 'ads_data_redaction', true);
      window.gtag('js', new Date());
      window.gtag('config', MEASUREMENT_ID, {
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
      googleInitialised = true;
    }

    if (!document.querySelector('script[data-l7-google-tag]')) {
      const script = document.createElement('script');
      script.async = true;
      script.dataset.l7GoogleTag = 'custom-events-only';
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
      document.head.appendChild(script);
    }

    return true;
  }

  function sendEvent(name, parameters) {
    if (!initialiseGoogleTag()) return false;
    window.gtag('event', name, { ...parameters, send_to: MEASUREMENT_ID });
    return true;
  }

  function pageType() {
    const bodyClass = String(document.body?.className || '');
    const match = bodyClass.match(/(?:^|\s)template-([a-z0-9-]+)/i);
    return match?.[1] || (window.location.pathname === '/' ? 'index' : 'page');
  }

  function randomEventId(prefix) {
    if (typeof window.crypto?.randomUUID === 'function') {
      return `${prefix}_${window.crypto.randomUUID()}`;
    }
    return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
  }

  function syncGoogleConsent() {
    if (!googleInitialised || typeof window.gtag !== 'function') return;
    window.gtag('consent', 'update', {
      analytics_storage: analyticsAllowed() ? 'granted' : 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }

  function isAppStoreLink(link) {
    try {
      const url = new URL(link.href, window.location.origin);
      return url.hostname.toLowerCase() === APP_STORE_HOST &&
        url.pathname.replace(/\/$/, '') === APP_STORE_PATH;
    } catch (_error) {
      return false;
    }
  }

  function ctaLocation(link) {
    if (link.dataset.l7CtaLocation) return link.dataset.l7CtaLocation;
    if (link.closest('.l7u-hero')) return 'home_hero';
    if (link.closest('.l7u-product')) return 'home_product';
    if (link.closest('.l7u-pricing')) return 'home_pricing';
    if (link.closest('.l7u-final')) return 'home_final';
    if (link.closest('[data-section-type="header"], header, .header__container')) return 'header';
    if (link.closest('footer, .footer__content')) return 'footer';
    if (link.closest('.l7pilot')) return 'founding_pilot';
    if (link.closest('.liva-page-cta, .l7-next-cta')) return 'page_closing_cta';
    return 'page_main';
  }

  function prepareAppStoreLinks(scope = document) {
    scope.querySelectorAll('a[href]').forEach((link) => {
      if (!isAppStoreLink(link)) return;
      link.dataset.l7CtaId = 'app_store_install';
      link.dataset.l7CtaLocation = ctaLocation(link);
    });
  }

  function readLeadAttempt() {
    if (leadAttempt) return leadAttempt;
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(LEAD_ATTEMPT_KEY));
      if (saved && /^lead_[a-z0-9_-]+$/i.test(saved.id) && /^page_[a-z0-9_-]+$/i.test(saved.documentId) &&
          saved.path === window.location.pathname &&
          Number.isFinite(saved.createdAt) && saved.createdAt <= Date.now() &&
          Date.now() - saved.createdAt < LEAD_ATTEMPT_MAX_AGE &&
          ['pending', 'sent', 'failed'].includes(saved.status)) leadAttempt = saved;
    } catch (_error) {
      // Without a surviving submission receipt, a new page fails closed.
    }
    return leadAttempt;
  }

  function saveLeadAttempt(attempt) {
    leadAttempt = attempt;
    try {
      window.sessionStorage.setItem(LEAD_ATTEMPT_KEY, JSON.stringify(attempt));
      return true;
    } catch (_error) {
      // A confirmation must be durably consumed before we dispatch it.
      return false;
    }
  }

  function beginLeadAttempt(formId) {
    if (formId !== 'founding-pilot' || isQaVisit() || !analyticsAllowed()) return;
    // A short-lived form receipt only: no field values, UTMs or advertising IDs.
    // Recording a submission attempt does not send an analytics event.
    saveLeadAttempt({
      id: randomEventId('lead'), documentId: leadDocumentId, path: window.location.pathname,
      createdAt: Date.now(), status: 'pending',
    });
  }

  function prepareForm(form) {
    if (preparedForms.has(form)) return;
    const formId = String(form.dataset.formId || '').trim().slice(0, 80);
    if (!formId) return;
    preparedForms.add(form);

    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      sendEvent('form_start', {
        form_id: formId,
        form_type: formId === 'founding-pilot' ? 'lead' : 'contact',
        page_type: pageType(),
      });
    };

    form.addEventListener('input', start, { capture: true, passive: true });
    form.addEventListener('change', start, { capture: true, passive: true });
    form.addEventListener('submit', () => {
      if (!form.checkValidity()) return;
      beginLeadAttempt(formId);
      sendEvent('form_submit', {
        form_id: formId,
        form_type: formId === 'founding-pilot' ? 'lead' : 'contact',
        form_status: 'attempted',
      });
    });
  }

  function prepareForms(scope = document) {
    scope.querySelectorAll('form[data-form-id]').forEach(prepareForm);
  }

  function recordServerConfirmedLeads(scope = document) {
    scope.querySelectorAll('form[data-form-id] [data-form-result="success"]').forEach((result) => {
      const form = result.closest('form[data-form-id]');
      const formId = String(form?.dataset.formId || '').trim().slice(0, 80);
      if (formId !== 'founding-pilot' || isQaVisit() || !analyticsAllowed()) return;

      const attempt = readLeadAttempt();
      // Shopify can render its success marker from contact_posted=true alone.
      // Require our own recent submission receipt, never a URL or click alone.
      if (!attempt || attempt.status !== 'pending' || attempt.documentId === leadDocumentId ||
          Date.now() - attempt.createdAt >= LEAD_ATTEMPT_MAX_AGE ||
          form.querySelector('[data-form-result="error"]')) return;

      // Consume before dispatch so retries, callbacks and refresh cannot duplicate
      // it. Fail closed if storage is blocked, rather than count on every reload.
      if (!saveLeadAttempt({ ...attempt, status: 'sent' })) {
        leadAttempt = attempt;
        return;
      }
      if (!sendEvent('generate_lead', {
        lead_id: attempt.id,
        form_id: formId,
        lead_type: 'founding_pilot',
      })) saveLeadAttempt(attempt);
    });
  }

  function scheduleServerConfirmedLeadRecording() {
    const confirmedLead = document.querySelector(
      'form[data-form-id="founding-pilot"] [data-form-result="success"]',
    );
    if (!confirmedLead || isQaVisit()) return;

    LEAD_CONFIRMATION_RETRY_DELAYS.forEach((delay) => {
      window.setTimeout(() => {
        if (!analyticsAllowed()) return;
        initialiseGoogleTag();
        recordServerConfirmedLeads();
      }, delay);
    });
  }

  function prepareScrollDepth() {
    let observed = false;
    const onScroll = () => {
      if (observed) return;
      const root = document.documentElement;
      const available = Math.max(0, root.scrollHeight - window.innerHeight);
      if (!available || window.scrollY / available < 0.9) return;
      observed = true;
      window.removeEventListener('scroll', onScroll);
      sendEvent('scroll', {
        page_type: pageType(),
        percent_scrolled: 90,
        measurement_source: 'liva7_manual',
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function prepare(scope = document) {
    prepareAppStoreLinks(scope);
    prepareForms(scope);
    if (scope.querySelector('form[data-form-id="founding-pilot"] [data-form-result="error"]')) {
      const attempt = readLeadAttempt();
      if (attempt?.status === 'pending') saveLeadAttempt({ ...attempt, status: 'failed' });
    }
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || !isAppStoreLink(link)) return;
    sendEvent('app_store_click', {
      cta_id: link.dataset.l7CtaId || 'app_store_install',
      cta_location: link.dataset.l7CtaLocation || ctaLocation(link),
      link_domain: APP_STORE_HOST,
      link_url_normalized: `https://${APP_STORE_HOST}${APP_STORE_PATH}`,
    });
  }, { capture: true });

  document.addEventListener('shopify:section:load', (event) => {
    prepare(event.target);
    scheduleServerConfirmedLeadRecording();
  });
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', handleCustomerPrivacyReady, { once: true });
  document.addEventListener('visitorConsentCollected', () => {
    if (analyticsAllowed()) {
      initialiseGoogleTag();
      scheduleServerConfirmedLeadRecording();
    }
    syncGoogleConsent();
  });
  window.addEventListener('pageshow', scheduleServerConfirmedLeadRecording);

  prepare();
  prepareScrollDepth();
  setQaStatus('measurement', 'loaded');
  LEAD_CONFIRMATION_RETRY_DELAYS.forEach((delay) => {
    window.setTimeout(loadCustomerPrivacyApi, delay);
  });
  scheduleServerConfirmedLeadRecording();
})();
